import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from '@remix-run/react';
import cs from 'classnames';
import { ScopedProvider, useRouteLoader } from '@lemon8/web-app-shared/ssr';
import {
  ArticleDetailUtil,
  DETAIL_POST_MODAL_NAME,
  EArticleUnavailableReason,
  IArticleUnavailableDetail,
  TArticleDetail,
} from 'shared/models';
import {
  $ArticleComments,
  $ArticleDetail,
  $ArticleRelatedHashtag,
  $ArticleRelatedPost,
  IArticleComments,
  IArticleRelatedHashtag,
  IArticleRelatedPost,
} from 'shared/atoms';
import { loadable } from 'jotai/utils';
import { FixBodyUtil } from '~/utils/fix-body';
import { useTimeout } from '~/utils/use-timeout';
import { ArticleSkeleton } from '~/skeleton/article';
import { PostDetailModalAtoms } from '~/components/post-detail-modal/atoms';
import { PostDetailModal } from '~/components/post-detail-modal';
import { useAtomValue } from 'jotai';
import { $ABTest } from '@lemon8/web-app-shared/atom';

import './style.scss';

interface DetailPostModalImplProps {
  gid: string;
  open: boolean;
  authorId: string;
}

const DetailPostModalImpl: FC<DetailPostModalImplProps> = ({ gid, open, authorId }) => {
  const [loading, setLoading] = useState(true);
  const [isArticleAvailable, setIsArticleAvailable] = useState(true);

  const { state: isRequestTimeout, destroy: destroyTimeout, endTimeout, startTimeout } = useTimeout(30_000);

  const navigate = useNavigate();

  const { parameters = {} } = useAtomValue($ABTest);
  const loadablePostDetail = useAtomValue(loadable($ArticleDetail(gid)));
  const loadableRelatedPost = useAtomValue(loadable($ArticleRelatedPost(gid)));
  const loadableComments = useAtomValue(loadable($ArticleComments(gid)));
  const loadableRelatedHashtag = useAtomValue(loadable($ArticleRelatedHashtag(gid)));

  const handleClose = (): void => {
    navigate(-1);
  };
  const postDetail = useMemo<TArticleDetail | IArticleUnavailableDetail>(() => {
    const defaultPostRes = {
      groupId: gid,
      unavailableReason: EArticleUnavailableReason.SERVER_ERROR,
    };
    if (isRequestTimeout) {
      return defaultPostRes;
    }

    if (loadablePostDetail.state === 'hasData') {
      return loadablePostDetail.data;
    }

    return defaultPostRes;
  }, [loadablePostDetail, isRequestTimeout]);

  const postComments = useMemo<IArticleComments>(() => {
    const defaultPostCommentsRes = {
      items: [],
      hasMore: false,
      cursor: 0,
    };
    if (isRequestTimeout) {
      return defaultPostCommentsRes;
    }

    if (loadableComments.state === 'hasData') {
      return loadableComments.data;
    }

    return defaultPostCommentsRes;
  }, [loadableComments, isRequestTimeout]);

  const relatedPosts = useMemo<IArticleRelatedPost>(() => {
    const defaultRelatedPostRes = {
      searchId: '',
      items: [],
      cursor: 0,
      hasMore: false,
    };
    if (isRequestTimeout) {
      return defaultRelatedPostRes;
    }

    if (loadableRelatedPost.state === 'hasData') {
      return loadableRelatedPost.data;
    }

    return defaultRelatedPostRes;
  }, [loadableRelatedPost, isRequestTimeout]);

  const relatedHashtag = useMemo<IArticleRelatedHashtag>(() => {
    const defaultRelatedHashtagRes = {
      items: [],
      sugWords: [],
      gptExtendWords: {
        highVolumeWords: [],
        longTailWords: [],
        relatedSEOWords: [],
      },
    };
    if (isRequestTimeout) {
      return defaultRelatedHashtagRes;
    }

    if (loadableRelatedHashtag.state === 'hasData') {
      return loadableRelatedHashtag.data;
    }

    return defaultRelatedHashtagRes;
  }, [loadableRelatedHashtag, isRequestTimeout]);

  const loader = useRouteLoader(
    'get-experience-related-post-main',
    { gid, userId: authorId || '', abParams: JSON.stringify(parameters) || '{}' },
    {
      atomFamilies: {
        $ArticleDetail,
        $ArticleComments,
        $ArticleRelatedPost,
        $ArticleRelatedHashtag,
      },
    },
  );

  useEffect(() => {
    if ((loader.state === 'idle' && loadablePostDetail.state === 'hasData') || isRequestTimeout) {
      setLoading(false);
      endTimeout();
    } else {
      setLoading(true);
    }
  }, [loader.state, postDetail, isRequestTimeout]);

  useEffect(() => {
    if (open && gid && authorId && loadablePostDetail.state !== 'hasData') {
      loader.load();
      startTimeout();
    }

    return () => {
      destroyTimeout();
    };
  }, [open]);

  useEffect(() => {
    if (loadablePostDetail.state === 'hasData') {
      const isAvailable = ArticleDetailUtil.isArticleAvailable(postDetail);
      if (!isAvailable) {
        setIsArticleAvailable(false);
        handleClose();
      }
    }
  }, [postDetail, loadablePostDetail]);

  return (
    <div
      className={cs('experience-post-detail-modal', {
        fixed: loading,
      })}
    >
      <div className="experience-post-detail-modal-content">
        {loading ? (
          <ArticleSkeleton />
        ) : (
          isArticleAvailable && (
            <ScopedProvider
              atomsWithHydration={[
                [PostDetailModalAtoms.$CurrentArticle, postDetail],
                [PostDetailModalAtoms.$CurrentArticleComments, postComments],
                [PostDetailModalAtoms.$CurrentArticleRelatedPost, relatedPosts],
                [PostDetailModalAtoms.$CurrentArticleRelatedHashtag, relatedHashtag],
              ]}
            >
              <PostDetailModal />
            </ScopedProvider>
          )
        )}
      </div>
    </div>
  );
};

export const DetailPostModal: FC = () => {
  const [open, setOpen] = useState(false);
  const [authorId, setAuthorId] = useState('');
  const [gid, setGid] = useState('');
  const location = useLocation();
  const navigationType = useNavigationType();

  const handleClose = (): void => {
    setOpen(false);
    setGid('');
    setAuthorId('');
  };

  const handleOpen = (gid: string, authorId: string): void => {
    setOpen(true);
    setGid(gid);
    setAuthorId(authorId);
  };

  useEffect(() => {
    if (open) {
      FixBodyUtil.partialFix();
    } else {
      FixBodyUtil.partialRestore();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      FixBodyUtil.partialRestore();
    };
  }, []);

  useEffect(() => {
    if (navigationType === 'PUSH') {
      const { enterName, gid, authorId } = location.state || {};

      if (enterName === DETAIL_POST_MODAL_NAME) {
        handleOpen(gid, authorId);
      }
    } else if (navigationType === 'POP' && open) {
      handleClose();
    }
  }, [navigationType, location, open]);

  if (!open || !gid) {
    return null;
  }

  return <DetailPostModalImpl gid={gid} open={open} authorId={authorId} />;
};
